import * as z from 'zod';

export const CreateStaffMemberRequest = z.object({
  staffMember: z
    .object({
      _id: z
        .string()
        .describe('Staff member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z.string().describe('Staff member name.').min(1).max(40),
      email: z
        .string()
        .describe(
          "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(320)
        .email()
        .optional()
        .nullable(),
      phone: z
        .string()
        .describe(
          "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(20)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
        )
        .max(500)
        .optional()
        .nullable(),
      mainMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z.string().describe("Staff member's main image."),
            }),
          ])
        )
        .describe('Staff media.')
        .optional(),
      resourceId: z
        .string()
        .describe(
          "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      resource: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          workingHoursSchedules: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe(
                    "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                shared: z
                  .boolean()
                  .describe(
                    "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
                  )
                  .optional(),
              })
            )
            .max(1)
            .optional(),
          eventsSchedule: z
            .object({
              _id: z
                .string()
                .describe(
                  'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
            )
            .optional(),
          usesDefaultWorkingHours: z
            .boolean()
            .describe(
              "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
            )
            .optional(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the resource is updated.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
        )
        .optional(),
      associatedWixIdentity: z
        .object({
          identificationData: z
            .intersection(
              z.object({
                contactId: z
                  .string()
                  .describe(
                    "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                }),
                z.object({
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  anonymousVisitorId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has not logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  memberId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  appId: z.never().optional(),
                  wixUserId: z
                    .string()
                    .describe(
                      'ID of a Wix user (site owner, contributor, etc.).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z
                    .string()
                    .describe('ID of an app.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
              ])
            )
            .describe(
              'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
            )
            .optional(),
          connectionStatus: z
            .enum([
              'UNKNOWN',
              'CONNECTED',
              'PENDING',
              'EXPIRED',
              'DISCONNECTED',
            ])
            .optional(),
          connection: z
            .object({
              status: z
                .enum([
                  'UNKNOWN',
                  'CONNECTED',
                  'PENDING',
                  'EXPIRED',
                  'DISCONNECTED',
                ])
                .optional(),
            })
            .describe(
              'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
            )
            .optional(),
        })
        .describe(
          'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the staff member.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
        )
        .optional(),
    })
    .describe('Staff member to create.'),
  options: z
    .object({
      fields: z
        .array(z.enum(['RESOURCE_DETAILS', 'ASSOCIATED_IDENTITY_STATUS']))
        .max(3)
        .optional(),
    })
    .describe('Options for creating the staff member.')
    .optional(),
});
export const CreateStaffMemberResponse = z.object({
  _id: z
    .string()
    .describe('Staff member ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Staff member name.')
    .min(1)
    .max(40)
    .optional()
    .nullable(),
  email: z
    .string()
    .describe(
      "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
    )
    .max(320)
    .email()
    .optional()
    .nullable(),
  phone: z
    .string()
    .describe(
      "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
    )
    .max(20)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe(
      'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
    )
    .max(500)
    .optional()
    .nullable(),
  mainMedia: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({ image: z.never().optional() }),
        z.object({ image: z.string().describe("Staff member's main image.") }),
      ])
    )
    .describe('Staff media.')
    .optional(),
  resourceId: z
    .string()
    .describe(
      "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  resource: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      workingHoursSchedules: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            shared: z
              .boolean()
              .describe(
                "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
              )
              .optional(),
          })
        )
        .max(1)
        .optional(),
      eventsSchedule: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
        )
        .optional(),
      usesDefaultWorkingHours: z
        .boolean()
        .describe(
          "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the resource is updated.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
    )
    .optional(),
  associatedWixIdentity: z
    .object({
      identificationData: z
        .intersection(
          z.object({
            contactId: z
              .string()
              .describe(
                "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
              appId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
              appId: z.never().optional(),
              anonymousVisitorId: z
                .string()
                .describe(
                  'ID of a site visitor that has not logged in to the site.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              wixUserId: z.never().optional(),
              appId: z.never().optional(),
              memberId: z
                .string()
                .describe(
                  'ID of a site visitor that has logged in to the site.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              appId: z.never().optional(),
              wixUserId: z
                .string()
                .describe('ID of a Wix user (site owner, contributor, etc.).')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
              appId: z
                .string()
                .describe('ID of an app.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
        )
        .optional(),
      connectionStatus: z
        .enum(['UNKNOWN', 'CONNECTED', 'PENDING', 'EXPIRED', 'DISCONNECTED'])
        .describe(
          'Deprecated: Connection status.\nAvailable only if you specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` array.'
        )
        .optional(),
      connection: z
        .object({
          status: z
            .enum([
              'UNKNOWN',
              'CONNECTED',
              'PENDING',
              'EXPIRED',
              'DISCONNECTED',
            ])
            .describe('Connection status of the Wix user and the staff member.')
            .optional(),
        })
        .describe(
          'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
        )
        .optional(),
    })
    .describe(
      'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Extensions enabling users to save custom data related to the staff member.'
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
    )
    .optional(),
});
export const GetStaffMemberRequest = z.object({
  staffMemberId: z
    .string()
    .describe('ID of the staff member to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .array(z.enum(['RESOURCE_DETAILS', 'ASSOCIATED_IDENTITY_STATUS']))
        .max(3)
        .optional(),
    })
    .describe('Option for retrieving the staff member.')
    .optional(),
});
export const GetStaffMemberResponse = z.object({
  _id: z
    .string()
    .describe('Staff member ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Staff member name.')
    .min(1)
    .max(40)
    .optional()
    .nullable(),
  email: z
    .string()
    .describe(
      "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
    )
    .max(320)
    .email()
    .optional()
    .nullable(),
  phone: z
    .string()
    .describe(
      "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
    )
    .max(20)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe(
      'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
    )
    .max(500)
    .optional()
    .nullable(),
  mainMedia: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({ image: z.never().optional() }),
        z.object({ image: z.string().describe("Staff member's main image.") }),
      ])
    )
    .describe('Staff media.')
    .optional(),
  resourceId: z
    .string()
    .describe(
      "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  resource: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      workingHoursSchedules: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            shared: z
              .boolean()
              .describe(
                "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
              )
              .optional(),
          })
        )
        .max(1)
        .optional(),
      eventsSchedule: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
        )
        .optional(),
      usesDefaultWorkingHours: z
        .boolean()
        .describe(
          "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the resource is updated.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
    )
    .optional(),
  associatedWixIdentity: z
    .object({
      identificationData: z
        .intersection(
          z.object({
            contactId: z
              .string()
              .describe(
                "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
              appId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
              appId: z.never().optional(),
              anonymousVisitorId: z
                .string()
                .describe(
                  'ID of a site visitor that has not logged in to the site.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              wixUserId: z.never().optional(),
              appId: z.never().optional(),
              memberId: z
                .string()
                .describe(
                  'ID of a site visitor that has logged in to the site.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              appId: z.never().optional(),
              wixUserId: z
                .string()
                .describe('ID of a Wix user (site owner, contributor, etc.).')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
              appId: z
                .string()
                .describe('ID of an app.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
        )
        .optional(),
      connectionStatus: z
        .enum(['UNKNOWN', 'CONNECTED', 'PENDING', 'EXPIRED', 'DISCONNECTED'])
        .describe(
          'Deprecated: Connection status.\nAvailable only if you specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` array.'
        )
        .optional(),
      connection: z
        .object({
          status: z
            .enum([
              'UNKNOWN',
              'CONNECTED',
              'PENDING',
              'EXPIRED',
              'DISCONNECTED',
            ])
            .describe('Connection status of the Wix user and the staff member.')
            .optional(),
        })
        .describe(
          'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
        )
        .optional(),
    })
    .describe(
      'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Extensions enabling users to save custom data related to the staff member.'
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
    )
    .optional(),
});
export const GetDeletedStaffMemberRequest = z.object({
  staffMemberId: z
    .string()
    .describe('ID of the deleted staff member to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .array(z.enum(['RESOURCE_DETAILS', 'ASSOCIATED_IDENTITY_STATUS']))
        .max(3)
        .optional(),
    })
    .describe('Options for getting the deleted staff member.')
    .optional(),
});
export const GetDeletedStaffMemberResponse = z.object({
  staffMember: z
    .object({
      _id: z
        .string()
        .describe('Staff member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Staff member name.')
        .min(1)
        .max(40)
        .optional()
        .nullable(),
      email: z
        .string()
        .describe(
          "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(320)
        .email()
        .optional()
        .nullable(),
      phone: z
        .string()
        .describe(
          "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(20)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
        )
        .max(500)
        .optional()
        .nullable(),
      mainMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z.string().describe("Staff member's main image."),
            }),
          ])
        )
        .describe('Staff media.')
        .optional(),
      resourceId: z
        .string()
        .describe(
          "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      resource: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          workingHoursSchedules: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe(
                    "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                shared: z
                  .boolean()
                  .describe(
                    "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
                  )
                  .optional(),
              })
            )
            .max(1)
            .optional(),
          eventsSchedule: z
            .object({
              _id: z
                .string()
                .describe(
                  'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
            )
            .optional(),
          usesDefaultWorkingHours: z
            .boolean()
            .describe(
              "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
            )
            .optional(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the resource is updated.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
        )
        .optional(),
      associatedWixIdentity: z
        .object({
          identificationData: z
            .intersection(
              z.object({
                contactId: z
                  .string()
                  .describe(
                    "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                }),
                z.object({
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  anonymousVisitorId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has not logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  memberId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  appId: z.never().optional(),
                  wixUserId: z
                    .string()
                    .describe(
                      'ID of a Wix user (site owner, contributor, etc.).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z
                    .string()
                    .describe('ID of an app.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
              ])
            )
            .describe(
              'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
            )
            .optional(),
          connectionStatus: z
            .enum([
              'UNKNOWN',
              'CONNECTED',
              'PENDING',
              'EXPIRED',
              'DISCONNECTED',
            ])
            .describe(
              'Deprecated: Connection status.\nAvailable only if you specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` array.'
            )
            .optional(),
          connection: z
            .object({
              status: z
                .enum([
                  'UNKNOWN',
                  'CONNECTED',
                  'PENDING',
                  'EXPIRED',
                  'DISCONNECTED',
                ])
                .describe(
                  'Connection status of the Wix user and the staff member.'
                )
                .optional(),
            })
            .describe(
              'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
            )
            .optional(),
        })
        .describe(
          'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the staff member.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
        )
        .optional(),
    })
    .describe('Retrieved staff member.')
    .optional(),
});
export const ListDeletedStaffMembersRequest = z.object({
  options: z
    .object({
      staffMemberIds: z.array(z.string()).max(100).optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Paging details.')
        .optional(),
      fields: z
        .array(z.enum(['RESOURCE_DETAILS', 'ASSOCIATED_IDENTITY_STATUS']))
        .max(3)
        .optional(),
    })
    .describe('Options for listing deleted staff members.')
    .optional(),
});
export const ListDeletedStaffMembersResponse = z.object({
  staffMembers: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Staff member ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Staff member name.')
          .min(1)
          .max(40)
          .optional()
          .nullable(),
        email: z
          .string()
          .describe(
            "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
          )
          .max(320)
          .email()
          .optional()
          .nullable(),
        phone: z
          .string()
          .describe(
            "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
          )
          .max(20)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe(
            'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
          )
          .max(500)
          .optional()
          .nullable(),
        mainMedia: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ image: z.never().optional() }),
              z.object({
                image: z.string().describe("Staff member's main image."),
              }),
            ])
          )
          .describe('Staff media.')
          .optional(),
        resourceId: z
          .string()
          .describe(
            "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        resource: z
          .object({
            _id: z
              .string()
              .describe(
                'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            workingHoursSchedules: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  shared: z
                    .boolean()
                    .describe(
                      "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
                    )
                    .optional(),
                })
              )
              .max(1)
              .optional(),
            eventsSchedule: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
              )
              .optional(),
            usesDefaultWorkingHours: z
              .boolean()
              .describe(
                "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
              )
              .optional(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the resource is updated.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
          )
          .optional(),
        associatedWixIdentity: z
          .object({
            identificationData: z
              .intersection(
                z.object({
                  contactId: z
                    .string()
                    .describe(
                      "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    appId: z.never().optional(),
                  }),
                  z.object({
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    appId: z.never().optional(),
                    anonymousVisitorId: z
                      .string()
                      .describe(
                        'ID of a site visitor that has not logged in to the site.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    appId: z.never().optional(),
                    memberId: z
                      .string()
                      .describe(
                        'ID of a site visitor that has logged in to the site.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    appId: z.never().optional(),
                    wixUserId: z
                      .string()
                      .describe(
                        'ID of a Wix user (site owner, contributor, etc.).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    appId: z
                      .string()
                      .describe('ID of an app.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe(
                'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
              )
              .optional(),
            connectionStatus: z
              .enum([
                'UNKNOWN',
                'CONNECTED',
                'PENDING',
                'EXPIRED',
                'DISCONNECTED',
              ])
              .describe(
                'Deprecated: Connection status.\nAvailable only if you specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` array.'
              )
              .optional(),
            connection: z
              .object({
                status: z
                  .enum([
                    'UNKNOWN',
                    'CONNECTED',
                    'PENDING',
                    'EXPIRED',
                    'DISCONNECTED',
                  ])
                  .describe(
                    'Connection status of the Wix user and the staff member.'
                  )
                  .optional(),
              })
              .describe(
                'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
              )
              .optional(),
          })
          .describe(
            'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extensions enabling users to save custom data related to the staff member.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe(
          'Number of items returned in the current response page.\n\nThis count reflects the actual number of items in the current result set,\nwhich may be less than the requested limit if fewer items are available.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor token for retrieving the next page of results.\n\nUse this token in subsequent requests to continue pagination forward.\nValue is `null` when on the last page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor token for retrieving the previous page of results.\n\nUse this token to navigate backwards through result pages.\nValue is `null` when on the first page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Navigation cursors for moving between result pages.\n\nContains `next` and `prev` cursor tokens for pagination. Use the `next` cursor\nto retrieve subsequent pages and `prev` cursor to go back to previous pages.\nLearn more about cursor paging in the [API Query Language guide](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging).'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates whether additional results are available beyond the current page.\n\n- `true`: More results exist and can be retrieved using the `next` cursor.\n- `false`: This is the final page of results.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const RemoveStaffMemberFromTrashBinRequest = z.object({
  staffMemberId: z
    .string()
    .describe('ID of the staff member to delete permanently.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const RemoveStaffMemberFromTrashBinResponse = z.object({});
export const UpdateStaffMemberRequest = z.object({
  _id: z
    .string()
    .describe('Staff member ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  staffMember: z
    .object({
      _id: z
        .string()
        .describe('Staff member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Staff member name.')
        .min(1)
        .max(40)
        .optional()
        .nullable(),
      email: z
        .string()
        .describe(
          "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(320)
        .email()
        .optional()
        .nullable(),
      phone: z
        .string()
        .describe(
          "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(20)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
        )
        .max(500)
        .optional()
        .nullable(),
      mainMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z.string().describe("Staff member's main image."),
            }),
          ])
        )
        .describe('Staff media.')
        .optional(),
      resourceId: z
        .string()
        .describe(
          "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      resource: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          workingHoursSchedules: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe(
                    "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                shared: z
                  .boolean()
                  .describe(
                    "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
                  )
                  .optional(),
              })
            )
            .max(1)
            .optional(),
          eventsSchedule: z
            .object({
              _id: z
                .string()
                .describe(
                  'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
            )
            .optional(),
          usesDefaultWorkingHours: z
            .boolean()
            .describe(
              "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
            )
            .optional(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the resource is updated.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
        )
        .optional(),
      associatedWixIdentity: z
        .object({
          identificationData: z
            .intersection(
              z.object({
                contactId: z
                  .string()
                  .describe(
                    "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                }),
                z.object({
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  anonymousVisitorId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has not logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  memberId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  appId: z.never().optional(),
                  wixUserId: z
                    .string()
                    .describe(
                      'ID of a Wix user (site owner, contributor, etc.).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z
                    .string()
                    .describe('ID of an app.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
              ])
            )
            .describe(
              'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
            )
            .optional(),
          connectionStatus: z
            .enum([
              'UNKNOWN',
              'CONNECTED',
              'PENDING',
              'EXPIRED',
              'DISCONNECTED',
            ])
            .optional(),
          connection: z
            .object({
              status: z
                .enum([
                  'UNKNOWN',
                  'CONNECTED',
                  'PENDING',
                  'EXPIRED',
                  'DISCONNECTED',
                ])
                .optional(),
            })
            .describe(
              'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
            )
            .optional(),
        })
        .describe(
          'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
        ),
      _createdDate: z
        .date()
        .describe(
          'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the staff member.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
        )
        .optional(),
    })
    .describe('The staff member to update'),
  options: z
    .object({
      fields: z
        .array(z.enum(['RESOURCE_DETAILS', 'ASSOCIATED_IDENTITY_STATUS']))
        .max(3)
        .optional(),
    })
    .describe('Options for updating the staff member.')
    .optional(),
});
export const UpdateStaffMemberResponse = z.object({
  _id: z
    .string()
    .describe('Staff member ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Staff member name.')
    .min(1)
    .max(40)
    .optional()
    .nullable(),
  email: z
    .string()
    .describe(
      "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
    )
    .max(320)
    .email()
    .optional()
    .nullable(),
  phone: z
    .string()
    .describe(
      "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
    )
    .max(20)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe(
      'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
    )
    .max(500)
    .optional()
    .nullable(),
  mainMedia: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({ image: z.never().optional() }),
        z.object({ image: z.string().describe("Staff member's main image.") }),
      ])
    )
    .describe('Staff media.')
    .optional(),
  resourceId: z
    .string()
    .describe(
      "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  resource: z
    .object({
      _id: z
        .string()
        .describe(
          'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      workingHoursSchedules: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            shared: z
              .boolean()
              .describe(
                "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
              )
              .optional(),
          })
        )
        .max(1)
        .optional(),
      eventsSchedule: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
        )
        .optional(),
      usesDefaultWorkingHours: z
        .boolean()
        .describe(
          "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the resource is updated.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
    )
    .optional(),
  associatedWixIdentity: z
    .object({
      identificationData: z
        .intersection(
          z.object({
            contactId: z
              .string()
              .describe(
                "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
              appId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
              appId: z.never().optional(),
              anonymousVisitorId: z
                .string()
                .describe(
                  'ID of a site visitor that has not logged in to the site.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              wixUserId: z.never().optional(),
              appId: z.never().optional(),
              memberId: z
                .string()
                .describe(
                  'ID of a site visitor that has logged in to the site.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              appId: z.never().optional(),
              wixUserId: z
                .string()
                .describe('ID of a Wix user (site owner, contributor, etc.).')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
              appId: z
                .string()
                .describe('ID of an app.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
        )
        .optional(),
      connectionStatus: z
        .enum(['UNKNOWN', 'CONNECTED', 'PENDING', 'EXPIRED', 'DISCONNECTED'])
        .describe(
          'Deprecated: Connection status.\nAvailable only if you specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` array.'
        )
        .optional(),
      connection: z
        .object({
          status: z
            .enum([
              'UNKNOWN',
              'CONNECTED',
              'PENDING',
              'EXPIRED',
              'DISCONNECTED',
            ])
            .describe('Connection status of the Wix user and the staff member.')
            .optional(),
        })
        .describe(
          'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
        )
        .optional(),
    })
    .describe(
      'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Extensions enabling users to save custom data related to the staff member.'
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
    )
    .optional(),
});
export const DeleteStaffMemberRequest = z.object({
  staffMemberId: z
    .string()
    .describe('ID of the staff member to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteStaffMemberResponse = z.object({});
export const QueryStaffMembersRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          description: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          resourceId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'associatedWixIdentity.identificationData.contactId': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'associatedWixIdentity.identificationData.wixUserId': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          email: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          phone: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          'resource.locationOptions.specificLocationOptions.businessLocations.locationId':
            z
              .object({ $hasAll: z.array(z.any()), $hasSome: z.array(z.any()) })
              .partial()
              .strict()
              .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'description',
                'resourceId',
                '_createdDate',
                '_updatedDate',
                'associatedWixIdentity.identificationData.contactId',
                'associatedWixIdentity.identificationData.wixUserId',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Information about filters, paging, and sorting.'),
  options: z
    .object({
      fields: z
        .array(z.enum(['RESOURCE_DETAILS', 'ASSOCIATED_IDENTITY_STATUS']))
        .max(3)
        .optional(),
    })
    .optional(),
});
export const QueryStaffMembersResponse = z.object({
  staffMembers: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Staff member ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Staff member name.')
          .min(1)
          .max(40)
          .optional()
          .nullable(),
        email: z
          .string()
          .describe(
            "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
          )
          .max(320)
          .email()
          .optional()
          .nullable(),
        phone: z
          .string()
          .describe(
            "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
          )
          .max(20)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe(
            'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
          )
          .max(500)
          .optional()
          .nullable(),
        mainMedia: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ image: z.never().optional() }),
              z.object({
                image: z.string().describe("Staff member's main image."),
              }),
            ])
          )
          .describe('Staff media.')
          .optional(),
        resourceId: z
          .string()
          .describe(
            "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        resource: z
          .object({
            _id: z
              .string()
              .describe(
                'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            workingHoursSchedules: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  shared: z
                    .boolean()
                    .describe(
                      "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
                    )
                    .optional(),
                })
              )
              .max(1)
              .optional(),
            eventsSchedule: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
              )
              .optional(),
            usesDefaultWorkingHours: z
              .boolean()
              .describe(
                "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
              )
              .optional(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the resource is updated.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
          )
          .optional(),
        associatedWixIdentity: z
          .object({
            identificationData: z
              .intersection(
                z.object({
                  contactId: z
                    .string()
                    .describe(
                      "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    appId: z.never().optional(),
                  }),
                  z.object({
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    appId: z.never().optional(),
                    anonymousVisitorId: z
                      .string()
                      .describe(
                        'ID of a site visitor that has not logged in to the site.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    appId: z.never().optional(),
                    memberId: z
                      .string()
                      .describe(
                        'ID of a site visitor that has logged in to the site.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    appId: z.never().optional(),
                    wixUserId: z
                      .string()
                      .describe(
                        'ID of a Wix user (site owner, contributor, etc.).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    appId: z
                      .string()
                      .describe('ID of an app.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe(
                'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
              )
              .optional(),
            connectionStatus: z
              .enum([
                'UNKNOWN',
                'CONNECTED',
                'PENDING',
                'EXPIRED',
                'DISCONNECTED',
              ])
              .describe(
                'Deprecated: Connection status.\nAvailable only if you specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` array.'
              )
              .optional(),
            connection: z
              .object({
                status: z
                  .enum([
                    'UNKNOWN',
                    'CONNECTED',
                    'PENDING',
                    'EXPIRED',
                    'DISCONNECTED',
                  ])
                  .describe(
                    'Connection status of the Wix user and the staff member.'
                  )
                  .optional(),
              })
              .describe(
                'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
              )
              .optional(),
          })
          .describe(
            'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extensions enabling users to save custom data related to the staff member.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe(
          'Number of items returned in the current response page.\n\nThis count reflects the actual number of items in the current result set,\nwhich may be less than the requested limit if fewer items are available.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor token for retrieving the next page of results.\n\nUse this token in subsequent requests to continue pagination forward.\nValue is `null` when on the last page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor token for retrieving the previous page of results.\n\nUse this token to navigate backwards through result pages.\nValue is `null` when on the first page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Navigation cursors for moving between result pages.\n\nContains `next` and `prev` cursor tokens for pagination. Use the `next` cursor\nto retrieve subsequent pages and `prev` cursor to go back to previous pages.\nLearn more about cursor paging in the [API Query Language guide](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging).'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates whether additional results are available beyond the current page.\n\n- `true`: More results exist and can be retrieved using the `next` cursor.\n- `false`: This is the final page of results.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const CountStaffMembersRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Filter to base the count upon.\nSee [Query Staff Members](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/query-staff-members)\nfor a complete list of supported filters.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CountStaffMembersResponse = z.object({
  count: z
    .number()
    .int()
    .describe('Total number of staff members matching the given filter.')
    .optional(),
});
export const ConnectStaffMemberToUserRequest = z.object({
  staffMemberId: z
    .string()
    .describe('ID of the staff member to connect to the Wix user.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      email: z
        .string()
        .describe(
          'Email of the Wix user to connect to the staff member.\n\nDefault: Email of the staff member.'
        )
        .max(320)
        .email()
        .optional()
        .nullable(),
      fields: z
        .array(z.enum(['RESOURCE_DETAILS', 'ASSOCIATED_IDENTITY_STATUS']))
        .max(3)
        .optional(),
    })
    .describe('Options for connecting the staff member to a Wix user.')
    .optional(),
});
export const ConnectStaffMemberToUserResponse = z.object({
  staffMember: z
    .object({
      _id: z
        .string()
        .describe('Staff member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Staff member name.')
        .min(1)
        .max(40)
        .optional()
        .nullable(),
      email: z
        .string()
        .describe(
          "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(320)
        .email()
        .optional()
        .nullable(),
      phone: z
        .string()
        .describe(
          "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(20)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
        )
        .max(500)
        .optional()
        .nullable(),
      mainMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z.string().describe("Staff member's main image."),
            }),
          ])
        )
        .describe('Staff media.')
        .optional(),
      resourceId: z
        .string()
        .describe(
          "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      resource: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          workingHoursSchedules: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe(
                    "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                shared: z
                  .boolean()
                  .describe(
                    "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
                  )
                  .optional(),
              })
            )
            .max(1)
            .optional(),
          eventsSchedule: z
            .object({
              _id: z
                .string()
                .describe(
                  'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
            )
            .optional(),
          usesDefaultWorkingHours: z
            .boolean()
            .describe(
              "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
            )
            .optional(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the resource is updated.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
        )
        .optional(),
      associatedWixIdentity: z
        .object({
          identificationData: z
            .intersection(
              z.object({
                contactId: z
                  .string()
                  .describe(
                    "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                }),
                z.object({
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  anonymousVisitorId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has not logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  memberId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  appId: z.never().optional(),
                  wixUserId: z
                    .string()
                    .describe(
                      'ID of a Wix user (site owner, contributor, etc.).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z
                    .string()
                    .describe('ID of an app.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
              ])
            )
            .describe(
              'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
            )
            .optional(),
          connectionStatus: z
            .enum([
              'UNKNOWN',
              'CONNECTED',
              'PENDING',
              'EXPIRED',
              'DISCONNECTED',
            ])
            .describe(
              'Deprecated: Connection status.\nAvailable only if you specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` array.'
            )
            .optional(),
          connection: z
            .object({
              status: z
                .enum([
                  'UNKNOWN',
                  'CONNECTED',
                  'PENDING',
                  'EXPIRED',
                  'DISCONNECTED',
                ])
                .describe(
                  'Connection status of the Wix user and the staff member.'
                )
                .optional(),
            })
            .describe(
              'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
            )
            .optional(),
        })
        .describe(
          'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the staff member.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
        )
        .optional(),
    })
    .describe('Updated staff member.')
    .optional(),
});
export const SearchStaffMembersRequest = z.object({
  search: z
    .object({
      filter: z
        .object({
          name: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          email: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          phone: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          description: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          resourceId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum(['name', 'email', 'phone', 'description', 'resourceId'])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Search criteria including filter, sort, and paging options.'),
  options: z
    .object({
      fields: z
        .array(z.enum(['RESOURCE_DETAILS', 'ASSOCIATED_IDENTITY_STATUS']))
        .max(3)
        .optional(),
    })
    .optional(),
});
export const SearchStaffMembersResponse = z.object({
  staffMembers: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Staff member ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Staff member name.')
          .min(1)
          .max(40)
          .optional()
          .nullable(),
        email: z
          .string()
          .describe(
            "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
          )
          .max(320)
          .email()
          .optional()
          .nullable(),
        phone: z
          .string()
          .describe(
            "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
          )
          .max(20)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe(
            'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
          )
          .max(500)
          .optional()
          .nullable(),
        mainMedia: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({ image: z.never().optional() }),
              z.object({
                image: z.string().describe("Staff member's main image."),
              }),
            ])
          )
          .describe('Staff media.')
          .optional(),
        resourceId: z
          .string()
          .describe(
            "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        resource: z
          .object({
            _id: z
              .string()
              .describe(
                'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            workingHoursSchedules: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  shared: z
                    .boolean()
                    .describe(
                      "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
                    )
                    .optional(),
                })
              )
              .max(1)
              .optional(),
            eventsSchedule: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
              )
              .optional(),
            usesDefaultWorkingHours: z
              .boolean()
              .describe(
                "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
              )
              .optional(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the resource is updated.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
          )
          .optional(),
        associatedWixIdentity: z
          .object({
            identificationData: z
              .intersection(
                z.object({
                  contactId: z
                    .string()
                    .describe(
                      "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    appId: z.never().optional(),
                  }),
                  z.object({
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    appId: z.never().optional(),
                    anonymousVisitorId: z
                      .string()
                      .describe(
                        'ID of a site visitor that has not logged in to the site.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    appId: z.never().optional(),
                    memberId: z
                      .string()
                      .describe(
                        'ID of a site visitor that has logged in to the site.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    appId: z.never().optional(),
                    wixUserId: z
                      .string()
                      .describe(
                        'ID of a Wix user (site owner, contributor, etc.).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    appId: z
                      .string()
                      .describe('ID of an app.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe(
                'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
              )
              .optional(),
            connectionStatus: z
              .enum([
                'UNKNOWN',
                'CONNECTED',
                'PENDING',
                'EXPIRED',
                'DISCONNECTED',
              ])
              .describe(
                'Deprecated: Connection status.\nAvailable only if you specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` array.'
              )
              .optional(),
            connection: z
              .object({
                status: z
                  .enum([
                    'UNKNOWN',
                    'CONNECTED',
                    'PENDING',
                    'EXPIRED',
                    'DISCONNECTED',
                  ])
                  .describe(
                    'Connection status of the Wix user and the staff member.'
                  )
                  .optional(),
              })
              .describe(
                'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
              )
              .optional(),
          })
          .describe(
            'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Extensions enabling users to save custom data related to the staff member.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe(
          'Number of items returned in the current response page.\n\nThis count reflects the actual number of items in the current result set,\nwhich may be less than the requested limit if fewer items are available.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor token for retrieving the next page of results.\n\nUse this token in subsequent requests to continue pagination forward.\nValue is `null` when on the last page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor token for retrieving the previous page of results.\n\nUse this token to navigate backwards through result pages.\nValue is `null` when on the first page of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Navigation cursors for moving between result pages.\n\nContains `next` and `prev` cursor tokens for pagination. Use the `next` cursor\nto retrieve subsequent pages and `prev` cursor to go back to previous pages.\nLearn more about cursor paging in the [API Query Language guide](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging).'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates whether additional results are available beyond the current page.\n\n- `true`: More results exist and can be retrieved using the `next` cursor.\n- `false`: This is the final page of results.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      "Cursor-based paging metadata for navigating through search results.\n\nContains pagination details including current cursor position, availability of additional\nresults, and item counts. Use the `next` cursor to retrieve subsequent result pages.\nNote: Staff members don't support aggregations, so only cursor and count information is provided."
    )
    .optional(),
  aggregationData: z
    .object({
      results: z
        .array(
          z.intersection(
            z.object({
              name: z
                .string()
                .describe(
                  'User-defined name of aggregation as derived from search request.'
                )
                .max(100)
                .optional(),
              type: z
                .enum(['UNKNOWN_AGGREGATION_TYPE', 'VALUE', 'RANGE', 'SCALAR'])
                .describe(
                  'Type of aggregation that must match specified kind as derived from search request.'
                )
                .optional(),
              fieldPath: z
                .string()
                .describe(
                  'Field to aggregate by as derived from search request.'
                )
                .max(200)
                .optional(),
            }),
            z.xor([
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
              }),
              z.object({
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                values: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities with this value.')
                            .optional(),
                        })
                      )
                      .max(250)
                      .optional(),
                  })
                  .describe('Value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                ranges: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          from: z
                            .number()
                            .describe('Inclusive lower bound of the range.')
                            .optional()
                            .nullable(),
                          to: z
                            .number()
                            .describe('Exclusive upper bound of the range.')
                            .optional()
                            .nullable(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities in this range.')
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                  })
                  .describe('Range aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalar: z
                  .object({
                    type: z
                      .enum([
                        'UNKNOWN_SCALAR_TYPE',
                        'COUNT_DISTINCT',
                        'MIN',
                        'MAX',
                      ])
                      .describe('Type of scalar aggregation.')
                      .optional(),
                    value: z
                      .number()
                      .describe('Value of the scalar aggregation.')
                      .optional(),
                  })
                  .describe('Scalar aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                groupedByValue: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(1000)
                            .optional(),
                          nestedResults: z
                            .intersection(
                              z.object({
                                name: z
                                  .string()
                                  .describe(
                                    'User-defined name of aggregation, matches the one specified in request.'
                                  )
                                  .max(100)
                                  .optional(),
                                type: z
                                  .enum([
                                    'UNKNOWN_AGGREGATION_TYPE',
                                    'VALUE',
                                    'RANGE',
                                    'SCALAR',
                                  ])
                                  .describe(
                                    'Type of aggregation that matches result.'
                                  )
                                  .optional(),
                                fieldPath: z
                                  .string()
                                  .describe(
                                    'Field to aggregate by, matches the one specified in request.'
                                  )
                                  .max(200)
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                }),
                                z.object({
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                  values: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            value: z
                                              .string()
                                              .describe('Value of the field.')
                                              .max(100)
                                              .optional(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Count of entities with this value.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(250)
                                        .optional(),
                                    })
                                    .describe('Value aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  scalar: z.never().optional(),
                                  ranges: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            from: z
                                              .number()
                                              .describe(
                                                'Inclusive lower bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            to: z
                                              .number()
                                              .describe(
                                                'Exclusive upper bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Count of entities in this range.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(50)
                                        .optional(),
                                    })
                                    .describe('Range aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z
                                    .object({
                                      type: z
                                        .enum([
                                          'UNKNOWN_SCALAR_TYPE',
                                          'COUNT_DISTINCT',
                                          'MIN',
                                          'MAX',
                                        ])
                                        .describe('Type of scalar aggregation.')
                                        .optional(),
                                      value: z
                                        .number()
                                        .describe(
                                          'Value of the scalar aggregation.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Scalar aggregation results.'),
                                }),
                              ])
                            )
                            .describe('Nested aggregations result data.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Group by value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                nested: z.never().optional(),
                dateHistogram: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe(
                              'Date in [ISO 8601 format](https://en.wikipedia.org/wiki/ISO_8601).'
                            )
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of documents in the bucket.')
                            .optional(),
                        })
                      )
                      .max(200)
                      .optional(),
                  })
                  .describe('Date histogram aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          results: z
                            .record(
                              z.string(),
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                  }),
                                  z.object({
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    value: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Value aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    range: z
                                      .object({
                                        from: z
                                          .number()
                                          .describe(
                                            'Inclusive lower bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        to: z
                                          .number()
                                          .describe(
                                            'Exclusive upper bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities in this range.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Range aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalar: z
                                      .object({
                                        value: z
                                          .number()
                                          .describe(
                                            'Value of the scalar aggregation.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Scalar aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Date histogram aggregation result.'
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe(
                              'Map of nested aggregation results, keyed by aggregation name.'
                            )
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Nested aggregation results.'),
              }),
            ])
          )
        )
        .max(10000)
        .optional(),
    })
    .describe(
      "Aggregation data results (currently reserved for future use).\n\nStaff member searches don't currently support aggregations. This field is reserved\nfor potential future aggregation capabilities such as grouping by working hours,\nlocation assignments, or user connection status."
    )
    .optional(),
});
export const DisconnectStaffMemberFromUserRequest = z.object({
  staffMemberId: z
    .string()
    .describe('ID of the staff member to disconnect from its Wix user.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .array(z.enum(['RESOURCE_DETAILS', 'ASSOCIATED_IDENTITY_STATUS']))
        .max(3)
        .optional(),
    })
    .describe('Options to disconnect the staff member from a Wix user.')
    .optional(),
});
export const DisconnectStaffMemberFromUserResponse = z.object({
  staffMember: z
    .object({
      _id: z
        .string()
        .describe('Staff member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Staff member name.')
        .min(1)
        .max(40)
        .optional()
        .nullable(),
      email: z
        .string()
        .describe(
          "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(320)
        .email()
        .optional()
        .nullable(),
      phone: z
        .string()
        .describe(
          "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(20)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
        )
        .max(500)
        .optional()
        .nullable(),
      mainMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z.string().describe("Staff member's main image."),
            }),
          ])
        )
        .describe('Staff media.')
        .optional(),
      resourceId: z
        .string()
        .describe(
          "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      resource: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          workingHoursSchedules: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe(
                    "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                shared: z
                  .boolean()
                  .describe(
                    "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
                  )
                  .optional(),
              })
            )
            .max(1)
            .optional(),
          eventsSchedule: z
            .object({
              _id: z
                .string()
                .describe(
                  'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
            )
            .optional(),
          usesDefaultWorkingHours: z
            .boolean()
            .describe(
              "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
            )
            .optional(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the resource is updated.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
        )
        .optional(),
      associatedWixIdentity: z
        .object({
          identificationData: z
            .intersection(
              z.object({
                contactId: z
                  .string()
                  .describe(
                    "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                }),
                z.object({
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  anonymousVisitorId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has not logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  memberId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  appId: z.never().optional(),
                  wixUserId: z
                    .string()
                    .describe(
                      'ID of a Wix user (site owner, contributor, etc.).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z
                    .string()
                    .describe('ID of an app.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
              ])
            )
            .describe(
              'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
            )
            .optional(),
          connectionStatus: z
            .enum([
              'UNKNOWN',
              'CONNECTED',
              'PENDING',
              'EXPIRED',
              'DISCONNECTED',
            ])
            .describe(
              'Deprecated: Connection status.\nAvailable only if you specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` array.'
            )
            .optional(),
          connection: z
            .object({
              status: z
                .enum([
                  'UNKNOWN',
                  'CONNECTED',
                  'PENDING',
                  'EXPIRED',
                  'DISCONNECTED',
                ])
                .describe(
                  'Connection status of the Wix user and the staff member.'
                )
                .optional(),
            })
            .describe(
              'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
            )
            .optional(),
        })
        .describe(
          'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the staff member.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
        )
        .optional(),
    })
    .describe('Updated staff member.')
    .optional(),
});
export const AssignWorkingHoursScheduleRequest = z.object({
  staffMemberId: z
    .string()
    .describe('ID of the staff member to assign the schedule to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  scheduleId: z
    .string()
    .describe(
      "ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto assign to the staff member.\n\nMust be either the staff member's event schedule ID or the working hour\nschedule ID for a business location."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .array(z.enum(['RESOURCE_DETAILS', 'ASSOCIATED_IDENTITY_STATUS']))
        .max(3)
        .optional(),
    })
    .describe("Options for setting the staff member's working hours schedule.")
    .optional(),
});
export const AssignWorkingHoursScheduleResponse = z.object({
  staffMember: z
    .object({
      _id: z
        .string()
        .describe('Staff member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Staff member name.')
        .min(1)
        .max(40)
        .optional()
        .nullable(),
      email: z
        .string()
        .describe(
          "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(320)
        .email()
        .optional()
        .nullable(),
      phone: z
        .string()
        .describe(
          "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(20)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
        )
        .max(500)
        .optional()
        .nullable(),
      mainMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z.string().describe("Staff member's main image."),
            }),
          ])
        )
        .describe('Staff media.')
        .optional(),
      resourceId: z
        .string()
        .describe(
          "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      resource: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          workingHoursSchedules: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe(
                    "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                shared: z
                  .boolean()
                  .describe(
                    "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
                  )
                  .optional(),
              })
            )
            .max(1)
            .optional(),
          eventsSchedule: z
            .object({
              _id: z
                .string()
                .describe(
                  'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
            )
            .optional(),
          usesDefaultWorkingHours: z
            .boolean()
            .describe(
              "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
            )
            .optional(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the resource is updated.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
        )
        .optional(),
      associatedWixIdentity: z
        .object({
          identificationData: z
            .intersection(
              z.object({
                contactId: z
                  .string()
                  .describe(
                    "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                }),
                z.object({
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  anonymousVisitorId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has not logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  memberId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  appId: z.never().optional(),
                  wixUserId: z
                    .string()
                    .describe(
                      'ID of a Wix user (site owner, contributor, etc.).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z
                    .string()
                    .describe('ID of an app.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
              ])
            )
            .describe(
              'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
            )
            .optional(),
          connectionStatus: z
            .enum([
              'UNKNOWN',
              'CONNECTED',
              'PENDING',
              'EXPIRED',
              'DISCONNECTED',
            ])
            .describe(
              'Deprecated: Connection status.\nAvailable only if you specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` array.'
            )
            .optional(),
          connection: z
            .object({
              status: z
                .enum([
                  'UNKNOWN',
                  'CONNECTED',
                  'PENDING',
                  'EXPIRED',
                  'DISCONNECTED',
                ])
                .describe(
                  'Connection status of the Wix user and the staff member.'
                )
                .optional(),
            })
            .describe(
              'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
            )
            .optional(),
        })
        .describe(
          'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the staff member.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
        )
        .optional(),
    })
    .describe('Updated staff member.')
    .optional(),
});
export const AssignCustomScheduleRequest = z.object({
  staffMemberId: z
    .string()
    .describe(
      'ID of the staff member for which to assign a working hour schedule.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  scheduleId: z
    .string()
    .describe(
      'ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nto assign to the staff member.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .array(z.enum(['RESOURCE_DETAILS', 'ASSOCIATED_IDENTITY_STATUS']))
        .max(3)
        .optional(),
    })
    .optional(),
});
export const AssignCustomScheduleResponse = z.object({
  staffMember: z
    .object({
      _id: z
        .string()
        .describe('Staff member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Staff member name.')
        .min(1)
        .max(40)
        .optional()
        .nullable(),
      email: z
        .string()
        .describe(
          "Staff member's email address.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(320)
        .email()
        .optional()
        .nullable(),
      phone: z
        .string()
        .describe(
          "Staff member's phone number.\nThis field is populated based on the `publiclyAccessible` setting in the [Staff Member Settings API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-member-settings/introduction).\nWhen the setting is disabled, this field is empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.\nIdentities with appropriate permissions can access this field regardless of the setting."
        )
        .max(20)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe(
          'Description for the staff member. For example,\n`Experienced nail technician specialized in gel and acrylic nails`.'
        )
        .max(500)
        .optional()
        .nullable(),
      mainMedia: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({ image: z.never().optional() }),
            z.object({
              image: z.string().describe("Staff member's main image."),
            }),
          ])
        )
        .describe('Staff media.')
        .optional(),
      resourceId: z
        .string()
        .describe(
          "Staff member's [resource ID](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction),\nidentical to `resource.id`."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      resource: z
        .object({
          _id: z
            .string()
            .describe(
              'ID of the [resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction)\nassociated with the staff member.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          workingHoursSchedules: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe(
                    "ID of the working hour [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Currently, each staff member can't have more than a\nsingle working hour schedule. Learn more about\n[how Wix Bookings uses the Calendar APIs](https://dev.wix.com/docs/api-reference/business-management/calendar/wix-bookings-integration)."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                shared: z
                  .boolean()
                  .describe(
                    "Whether the working hour schedule is shared with the business. If this field\nisn't available, the schedule isn't shared but specific for the staff member."
                  )
                  .optional(),
              })
            )
            .max(1)
            .optional(),
          eventsSchedule: z
            .object({
              _id: z
                .string()
                .describe(
                  'ID of the event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nassociated with the staff member. Learn more about\n_how Wix Bookings uses the Calendar APIs_\n[Wix Bookings](https://dev.wix.com/docs/rest/business-management/calendar/wix-bookings-integration).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Event [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)\nof the staff member.'
            )
            .optional(),
          usesDefaultWorkingHours: z
            .boolean()
            .describe(
              "Whether the staff member works according to business's [default location](https://dev.wix.com/docs/rest/business-management/locations/introduction)\nopening hours.\n\n`false`: The staff has custom working hours.\n\nDefault: `true`"
            )
            .optional(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the resource is updated.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Details about the [resource object](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/resource-object)\nassociated with the staff member. Available only if you specify `RESOURCE_DETAILS`\nin the `fields` array.'
        )
        .optional(),
      associatedWixIdentity: z
        .object({
          identificationData: z
            .intersection(
              z.object({
                contactId: z
                  .string()
                  .describe(
                    "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system. Retrievable via the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/introduction)"
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                }),
                z.object({
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  anonymousVisitorId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has not logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z.never().optional(),
                  memberId: z
                    .string()
                    .describe(
                      'ID of a site visitor that has logged in to the site.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  appId: z.never().optional(),
                  wixUserId: z
                    .string()
                    .describe(
                      'ID of a Wix user (site owner, contributor, etc.).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  anonymousVisitorId: z.never().optional(),
                  memberId: z.never().optional(),
                  wixUserId: z.never().optional(),
                  appId: z
                    .string()
                    .describe('ID of an app.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
              ])
            )
            .describe(
              'Information about the identity connected to the staff member. Available only\nif the staff member is connected to a Wix user. Learn more about [identities](https://dev.wix.com/docs/rest/articles/getting-started/about-identities).'
            )
            .optional(),
          connectionStatus: z
            .enum([
              'UNKNOWN',
              'CONNECTED',
              'PENDING',
              'EXPIRED',
              'DISCONNECTED',
            ])
            .describe(
              'Deprecated: Connection status.\nAvailable only if you specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` array.'
            )
            .optional(),
          connection: z
            .object({
              status: z
                .enum([
                  'UNKNOWN',
                  'CONNECTED',
                  'PENDING',
                  'EXPIRED',
                  'DISCONNECTED',
                ])
                .describe(
                  'Connection status of the Wix user and the staff member.'
                )
                .optional(),
            })
            .describe(
              'Connection status. Available only if you specify `ASSOCIATED_IDENTITY_STATUS`\nin the `fields` array.'
            )
            .optional(),
        })
        .describe(
          'Identity of the Wix user associated with the staff member. Learn more about\n[identities](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the staff member is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the staff member.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Time the staff member was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Time the staff member was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extensions enabling users to save custom data related to the staff member.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags allowing you to classify staff members. Learn more about [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction).'
        )
        .optional(),
    })
    .describe('Updated staff member.')
    .optional(),
});
export const BulkUpdateStaffMemberTagsRequest = z.object({
  ids: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to add to the staff members.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to remove from the staff members.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateStaffMemberTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata about an individual update operation.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkUpdateStaffMemberTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe(
      'Filter to base the update upon.\nSee [Query Staff Members](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/query-staff-members)\nfor a complete list of supported filters.'
    ),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to add to the staff members.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to remove from the staff members.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateStaffMemberTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe('Job ID for the bulk update operation.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
